<template>
  <div :class="['text-grey-9 q-px-md q-pb-md', sectionData.class]">
    <div id="compare-table">
      <q-list :separator="false">
        <DCompareListRows
          rowClass="title-rows"
          :keys="keys"
          :data="title"
          v-if="title"
        />
        <DCompareListRows
          :keys="keys"
          :data="beforeHidden"
          v-if="beforeHidden"
        />

        <q-expansion-item
          class="hidden-rows-accordion"
          :expand-separator="false"
          v-model="showHidden"
        >
          <template #header>
            <q-item
              class="full-width"
              :style="{
                backgroundColor: hiddenRowLightenColor,
              }"
            >
              <template v-for="(key, index) in keys">
                <div
                  class="item column items-center justify-center"
                  :key="index"
                  :style="{
                    width: `calc(100% / ${keys.length})`,
                  }"
                >
                  <q-separator
                    class="full-height"
                    vertical
                    v-if="index"
                    :style="{ backgroundColor: getSeparatorColor(index) }"
                  />

                  <q-item-section
                    :class="[
                      'full-width',
                      `text-${!index ? 'start' : 'center'}`,
                    ]"
                  >
                    <div
                      class="row items-center justify-center no-wrap expand-hidden"
                      :style="{
                        color: hiddenRowColor,
                      }"
                      v-if="!index"
                    >
                      <q-icon
                        class="icon"
                        :name="!showHidden ? 'expand_more' : 'expand_less'"
                        size="12px"
                        :style="{
                          backgroundColor: hiddenRowColor,
                          color: hiddenRowContrast,
                        }"
                      />
                      <div class="text q-ml-sm">
                        {{ !showHidden ? "show details" : "hide details" }}
                      </div>
                    </div>
                    <div class="text" v-else></div>
                  </q-item-section>
                </div>
              </template>
            </q-item>
          </template>

          <template #default>
            <DCompareListRows
              rowClass="hidden-rows"
              :keys="keys"
              :data="hidden"
              v-if="hidden"
            />
          </template>
        </q-expansion-item>

        <DCompareListRows
          rowClass="afterHidden-row"
          :keys="keys"
          :data="afterHidden"
          v-if="afterHidden"
        />
      </q-list>

      <div
        :class="['legend', { first: !index }]"
        v-html="item"
        :key="index"
        v-for="(item, index) in legend"
      ></div>
    </div>
  </div>
</template>

<script>
import DCompareListRows from "../../components/DCompareListRows/DCompareListRows.vue";
import { colors } from "quasar";
const { changeAlpha, brightness, getBrand, lighten } = colors;

export default {
  name: "DCompareList",
  components: { DCompareListRows },
  data() {
    return {
      showHidden: false,
    };
  },
  props: {
    sectionData: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    keys: {
      get() {
        return this.sectionData.keys;
      },
    },
    title: {
      get() {
        return this.sectionData.rows.title;
      },
    },
    beforeHidden: {
      get() {
        return this.sectionData.rows.beforeHidden;
      },
    },
    hidden: {
      get() {
        return this.sectionData.rows.hidden;
      },
    },
    afterHidden: {
      get() {
        return this.sectionData.rows.afterHidden;
      },
    },
    legend: {
      get() {
        return this.sectionData.legend ?? [];
      },
    },
    hiddenRowColor: {
      get() {
        return this.sectionData.hiddenOptions?.color ?? getBrand("secondary");
      },
    },
    hiddenRowContrast: {
      get() {
        const result = brightness(this.hiddenRowColor);
        return result < 128 ? "white" : "#333";
      },
    },
    hiddenRowLightenColor: {
      get() {
        return lighten(this.hiddenRowColor, 90);
      },
    },
  },
  methods: {
    getSeparatorColor(index) {
      return index !== this.keys.length - 1
        ? "#E0E0E0"
        : changeAlpha("#E0E0E0", 0.5);
    },
  },
};
</script>

<style lang="scss">
#compare-table {
  letter-spacing: 0.2px;
  font-family: Open Sans;

  .q-item:not(.q-item--clickable) {
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
    min-height: 40px;

    &:first-child {
      border-top: 1px solid #e0e0e0;
    }

    &:last-child {
      border-bottom: 1px solid #e0e0e0;
    }

    background-color: white;
    font-weight: 400;
    padding: 8px 0;
    font-size: 10px;
    line-height: 14px;
    text-transform: capitalize;

    &.title-rows,
    &.hidden-rows:nth-child(even) {
      background-color: #f5f5f5;
    }

    > .item {
      &:first-child {
        font-size: 8px;
        line-height: 12px;
        font-weight: bold;
        text-transform: uppercase;
      }

      &:nth-child(2) {
        font-weight: bold;
      }
    }

    .title {
      font-family: "Times New Roman";
      font-size: 10px;
      font-weight: 700;
      margin-bottom: 6px;
    }

    &.justify-center {
      .q-item__section {
        justify-content: center !important;
      }
    }

    &.justify-start {
      .q-item__section {
        justify-content: start !important;
      }
    }

    .q-item__section {
      padding: 0 6px;
    }

    &.title-rows {
      .item:last-child .q-item__section {
        padding-right: 2px;
      }
    }

    &.title {
      padding: 12px 0;
      font-size: 8px;
      line-height: 12px;
    }

    &.afterHidden-row {
      border-color: #333;

      &:first-child {
        background-color: #333;
        color: white;
      }
    }
  }

  .hidden-rows-accordion {
    .q-expansion-item__container {
      > .q-item {
        border-top: unset;
        > .q-item__section {
          display: none !important;
        }
      }
    }

    .q-item--clickable {
      padding: 0;
    }

    .text {
      text-transform: lowercase;
      font-weight: 400;
    }

    .expand-hidden {
      .text {
        font-size: 8px;
        font-weight: 700;
        line-height: 12px;
        text-transform: uppercase;
      }
    }
  }

  .legend {
    font-size: 7px;
    font-weight: 400;
    line-height: 12px;

    &.first {
      margin-top: 12px;
    }
  }
}
</style>
